import { lazy, useRef } from 'react'
import { useAuth } from '@/app/providers/auth-context'
import { chooseHome } from '@/features/home/personal/homeChoice'
import { homeViewer, prefetchPersonalHome } from '@/features/home/personal/homePrefetch'
import { hasSessionHint } from '@/lib/auth/tokenStore'
import { HomePage } from '@/pages/home/Page'

// The personal home is its own chunk; the marketing home stays eager, because its H1 is
// the LCP and nothing about the signed-out load may get slower.
const PersonalHomePage = lazy(() =>
  import('@/pages/me/home/Page').then((m) => ({ default: m.PersonalHomePage })),
)

/**
 * `/` — two pages chosen by who is visiting (docs/ux/2026-09-23-home-split.md §3.1).
 * No session hint: the marketing home, immediately — the only path a crawler can take.
 * A hint: the personal home's skeleton while the session check runs, then the personal
 * home, or marketing if that session turns out to have expired. `RoutedShell` asks the
 * same `chooseHome`, so the chrome and the page always agree.
 */
export function HomeEntry() {
  const { state, me } = useAuth()
  const choice = chooseHome(state, hasSessionHint())
  // Start the four personal reads beside the lazy chunk, once per viewer per visit — a
  // later re-render (a token refresh) must not start reads nobody will consume.
  const prefetchedFor = useRef('')
  if (choice === 'personal') {
    const viewer = homeViewer(me)
    if (prefetchedFor.current !== viewer) {
      prefetchedFor.current = viewer
      prefetchPersonalHome(viewer)
    }
  }
  return choice === 'marketing' ? <HomePage /> : <PersonalHomePage />
}
